import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Globe, Zap, Truck, TreePine } from 'lucide-react';

const Location = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const advantages = [
    {
      icon: Navigation,
      title: 'Acceso Vial Directo',
      description: 'Conectados con la Ruta PY02 y acceso directo a Ciudad del Este y Foz do Iguaçu.',
    },
    {
      icon: Globe,
      title: 'Vía Fluvial Paraná',
      description: 'Acceso al río Paraná para logística internacional hacia el Atlántico.',
    },
    {
      icon: Zap,
      title: 'Energía de Itaipú',
      description: 'Cercanía a la zona industrial de Alto Paraná con acceso a energía limpia.',
    },
    {
      icon: TreePine,
      title: 'Biodiversidad Única',
      description: 'Ubicación estratégica en el corredor de la Selva Atlántica del Alto Paraná.',
    },
    {
      icon: Truck,
      title: 'MERCOSUR',
      description: 'Posición estratégica dentro del bloque comercial para operaciones sin fronteras.',
    },
  ];

  return (
    <section id="ubicacion" style={{
      background: 'var(--dark)',
      padding: 'var(--space-3xl) 0',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background decoration */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '-15%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(16,185,129,0.04) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      <div className="container" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 80px' }}
        >
          <span style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '50px',
            color: 'var(--primary-400)',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '20px',
          }}>
            Ubicación Estratégica
          </span>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: '20px',
          }}>
            Naranjal,{' '}
            <span className="text-gradient">Alto Paraná</span>
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
          }}>
            En el corazón productivo del este paraguayo, con acceso privilegiado 
            a rutas comerciales internacionales y la mayor biodiversidad del país.
          </p>
        </motion.div>

        {/* Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'start',
        }} className="location-grid">
          {/* Map / Visual */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div style={{
              background: 'var(--dark-800)',
              borderRadius: '24px',
              padding: '40px',
              border: '1px solid var(--border)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Stylized map representation */}
              <div style={{
                background: 'var(--dark-900)',
                borderRadius: '16px',
                padding: '40px',
                textAlign: 'center',
                position: 'relative',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {/* Decorative map lines */}
                <svg style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0.1,
                }}>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--primary-500)" strokeWidth="0.5"/>
                  </pattern>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>

                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'var(--gradient-primary)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                  boxShadow: 'var(--shadow-glow)',
                  animation: 'pulse-glow 3s infinite',
                }}>
                  <MapPin size={36} color="white" />
                </div>

                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  marginBottom: '8px',
                }}>
                  Naranjal, Alto Paraná
                </h3>
                <p style={{
                  color: 'var(--text-muted)',
                  marginBottom: '24px',
                }}>
                  Paraguay — Frontera con Brasil
                </p>

                <div style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}>
                  <span style={{
                    padding: '8px 16px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: 'var(--primary-400)',
                  }}>
                    Lat: -25.95°
                  </span>
                  <span style={{
                    padding: '8px 16px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: 'var(--primary-400)',
                  }}>
                    Long: -54.90°
                  </span>
                </div>

                <p style={{
                  marginTop: '24px',
                  fontSize: '0.85rem',
                  color: 'var(--dark-500)',
                }}>
                  <MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
                  Mapa interactivo disponible próximamente
                </p>
              </div>
            </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '2rem',
              marginBottom: '20px',
            }}>
              Ventajas de Nuestra <span className="text-gradient-accent">Ubicación</span>
            </h3>

            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: 1.8,
              marginBottom: '40px',
              fontSize: '1.05rem',
            }}>
              Ubicados en <strong style={{ color: 'var(--primary-400)' }}>Naranjal</strong>, distrito de Alto Paraná, 
              nuestra posición geográfica nos otorga ventajas logísticas incomparables para el comercio 
              bilateral entre Paraguay y Brasil, además de estar en la región de mayor biodiversidad del país.
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              {advantages.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: 30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    padding: '20px',
                    background: 'var(--dark-800)',
                    borderRadius: '14px',
                    border: '1px solid var(--border)',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                    e.currentTarget.style.transform = 'translateX(8px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <item.icon size={22} style={{ color: 'var(--primary-400)' }} />
                  </div>
                  <div>
                    <h4 style={{
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      marginBottom: '4px',
                    }}>
                      {item.title}
                    </h4>
                    <p style={{
                      color: 'var(--text-muted)',
                      fontSize: '0.9rem',
                      lineHeight: 1.5,
                    }}>
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .location-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Location;
